import axios from 'axios';
import { API_BASE_URL, CONVERSATIONAL_API_BASE_URL } from '../config/constants';
import { APP_BASE } from '../config/base';
import { inShell, attachShellAuth } from '../shell';
import { standalone, refresh as standaloneRefresh, goSignIn } from '../standalone';


// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Create API instance for file uploads (no default Content-Type header)
const uploadApi = axios.create({
  baseURL: API_BASE_URL
});

// Create public API instance for pre-screening tests (no auth required)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Create conversational API instance (always localhost for development)
const conversationalApi = axios.create({
  baseURL: CONVERSATIONAL_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add auth token to upload requests
uploadApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Silent token refresh on 401.
 *
 * The gateway's access token lives fifteen minutes; before this, the first
 * request after it expired logged the user straight out. Now a 401 tries the
 * refresh token once, and only a FAILED refresh ends the session.
 *
 * Two things this must get right, both because the gateway rotates the refresh
 * token (using one returns a new one and revokes the old):
 *   - Store what comes back. The next refresh must use the new token.
 *   - Never refresh twice at once. A burst of calls all hitting 401 together
 *     would each try to spend the same refresh token; the second spend looks
 *     like a replayed token and the gateway kills the whole session. So all
 *     concurrent 401s wait on ONE in-flight refresh.
 */
let refreshInFlight = null;

function forceLogout() {
  // The school's own Hiring app has no sign-in here: back to the school's.
  if (standalone) { goSignIn(); return; }
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (!/\/login$/.test(window.location.pathname)) window.location.href = `${APP_BASE}/login`;
}

async function refreshAccessToken() {
  // The school's own Hiring app renews at the gateway on its own address.
  if (standalone) return standaloneRefresh();
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('no refresh token');
  // A bare axios call, not `api`, so it cannot recurse through this interceptor.
  const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  const data = res.data?.data || {};
  if (!data.token) throw new Error('refresh returned no token');
  localStorage.setItem('token', data.token);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  return data.token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Inside the shell there is no refresh token and no login here: the
    // shell-auth interceptor below retries a 401 once with a new token.
    if (inShell) return Promise.reject(error);

    const original = error.config;
    const isAuthCall = original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/login');

    if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        // One shared refresh for every request that hit 401 at once.
        refreshInFlight = refreshInFlight || refreshAccessToken().finally(() => { refreshInFlight = null; });
        const newToken = await refreshInFlight;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);           // replay the original request, now authorised
      } catch (e) {
        forceLogout();                  // refresh itself failed — the session is truly over
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && !isAuthCall) forceLogout();
    return Promise.reject(error);
  }
);

// Inside the Equall shell: token from the shell on every call, one retry on 401.
// No-ops outside the shell.
attachShellAuth(api);
attachShellAuth(uploadApi);

// Add response interceptor for public API (no auth redirect)
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Public API Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for conversational API (no auth redirect)
conversationalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Conversational API Error:', error);
    return Promise.reject(error);
  }
);

api.getHiringPipeline = async (userId) => {
  try {
      console.log('API Service: Fetching hiring pipeline for userId:', userId);
      
      if (!userId) {
          throw new Error('User ID is required');
      }

      const response = await api.get(`/admin/users/${userId}/hiring-pipeline`);
      console.log('API Service: Received response:', response.data);

      if (!response.data) {
          throw new Error('No data received from server');
      }

      return {
          success: true,
          jobs: Array.isArray(response.data.jobs) ? response.data.jobs : [],
          count: response.data.count || 0
      };
  } catch (error) {
      console.error('API Service Error:', error);
      throw error;
  }
};

api.allocateTokens = async (userId, amount, expiryDate, reason) => {
  const response = await api.post('/admin/users/tokens/allocate', {
      userId,
      amount,
      expiryDate,
      reason
  });
  return response.data;
};

export const getUserActivity = async (userId) => {
  try {
    const response = await api.get(`/admin/users/${userId}/activity`);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};


export { publicApi, conversationalApi, uploadApi };
export default api; 